import WMSCapabilities from 'ol/format/WMSCapabilities'

import type { ConfigLayerCapabilityOptions } from './ConfigLayerCapability'
import type { ConfigLayerWMSOptions } from './ConfigLayerWMS'
import type { ServerType } from './ServerType'
import type { ConfigLayerOptions } from '../ConfigLayer'
import type { Geometry } from 'geojson'

import * as PoziUtils from 'api/utils'

import { ConfigLayerCapability } from './ConfigLayerCapability'
import { ConfigLayerCatalog } from './ConfigLayerCatalog'
import { ConfigLayerWMS } from './ConfigLayerWMS'
import { ConfigLayerType } from '../ConfigLayerType'

export interface ConfigLayerCapabilityWMSOptions extends ConfigLayerOptions {
  type: ConfigLayerType.WMS_GET_CAPABILITIES
  serverType?: ServerType
  layers: ConfigLayerWMSOptions[]
}

export class ConfigLayerCapabilityWMS extends ConfigLayerCatalog {
  static async fetchCapability (capabilityOptions: ConfigLayerCapabilityOptions) {
    const url = PoziUtils.getLayerUrl(capabilityOptions)

    if (url) {
      try {
        const text = await ConfigLayerCapability.fetchUrl(url)
        if (text) {
          const wmsCapability = this.getCapabilities(text)
          if (wmsCapability) {
            const configLayerCapabilityWMSOptions: ConfigLayerCapabilityWMSOptions = {
              type: ConfigLayerType.WMS_GET_CAPABILITIES,
              serverType: capabilityOptions.serverType,
              title: capabilityOptions.title,
              titleid: capabilityOptions.titleid,
              group: capabilityOptions.group,
              groupid: capabilityOptions.groupid,
              layers: this.getLayers(capabilityOptions, wmsCapability)
            }
            return configLayerCapabilityWMSOptions
          }
        }
      } catch (error) {
        console.error('Error fetching capabilities: ', url, error)
      }
    } else {
      console.error('Error getting url property from config to request WMS GetCapabilities: ', capabilityOptions)
    }
  }

  private static getCapabilities (text: string) {
    const parser = new WMSCapabilities()
    const wmsCapability: WMSCapability = parser.read(text)
    // console.debug('WMS Capabilities: ', wmsCapability)

    return wmsCapability
  }

  private static getChildLayers (
    capabilityOptions: ConfigLayerCapabilityOptions,
    wmsCapability: WMSCapability,
    layers: WMSLayer[],
    layerPathArray: string[] = [capabilityOptions.title],
    parentConf?: ConfigLayerWMSOptions
  ) {
    const configObjs: ConfigLayerWMSOptions[] = []

    // For layer filtering below
    const hasLayerFilter = this.hasLayerFilter(capabilityOptions)
    const layerFilterArray = this.getLayerFilterArray(capabilityOptions)

    // Keep track of layers that did not match filter and report them
    const unMatchedLayers: string[] = []

    layers.forEach((layer, index: number) => {
      const configLayer = ConfigLayerWMS.getLayer(capabilityOptions, wmsCapability, layer, index)

      if (!configLayer) {
        return
      }

      // Keep track of parent-child relationship through titleid
      // Note: we cannot use a direct reference to the parent object due to
      //       immer only allowing unidirectional trees:
      //       https://immerjs.github.io/immer/pitfalls/#immer-only-supports-unidirectional-trees
      if (parentConf) {
        configLayer.parentid = parentConf.titleid
      }

      // Layer title 'breadcrumb'
      const childLayerPathArray = [...layerPathArray, configLayer.title]
      configLayer.layerPathArray = childLayerPathArray

      const childLayers = layer.Layer
      if (childLayers) {
        // console.debug('WMS getChildLayers - layer has children. Recursing.', configLayer.titleid, configLayer, layer.Layer)
        configLayer.layers = this.getChildLayers(capabilityOptions, wmsCapability, childLayers)
      }

      // If we encounter an empty layer group, ignore
      if (configLayer.layers && configLayer.layers.length === 0) {
        return
      }

      // We are at a leaf node. Determine whether this layer matches any provided layer filter and ignore if
      // there is no match
      if (!childLayers && hasLayerFilter) {
        const isMatch = this.layerMatchesFilter(configLayer, layerFilterArray)
        if (!isMatch) {
          unMatchedLayers.push(configLayer.title)

          return
        }
      }

      // If a catalogue is configured to be flattened, we will add all
      // child leaf layers individually instead of a tree structure
      const flatten = capabilityOptions.flatten

      if (flatten) {
        // console.debug('flatten', configLayer.titleid, flatten, !!configLayer.layers, configLayer, capabilityOptions)

        // Add group to flattened layers so it will be picked up for rendering
        configLayer.group = capabilityOptions.group
        configLayer.groupid = capabilityOptions.groupid

        // Do the same for all its child layers
        if (configLayer.layers) {
          for (const childLayerConf of configLayer.layers) {
            // Add group to flattened child layers so it will be picked up for rendering
            childLayerConf.group = capabilityOptions.group
            childLayerConf.groupid = capabilityOptions.groupid

            configObjs.push(childLayerConf)
          }
        } else {
          configObjs.push(configLayer)
        }
      } else {
        configObjs.push(configLayer)
      }
    })

    if (unMatchedLayers.length > 0) {
      console.debug(`ConfigLayerCapabilityWMS getLayers - ${capabilityOptions.titleid} - ${unMatchedLayers.length} layers did not match filter '${capabilityOptions.layerFilter}'`, unMatchedLayers)
    }

    return configObjs
  }

  private static getLayers (capabilityOptions: ConfigLayerCapabilityOptions, wmsCapability: WMSCapability) {
    const layers = wmsCapability.Capability?.Layer?.Layer || // Most WMS servers
      (wmsCapability.Capability?.Layer ? [wmsCapability.Capability.Layer as WMSLayer] : []) // e.g. ArcGIS Online WMS server

    let configObjs = this.getChildLayers(capabilityOptions, wmsCapability, layers)
    // console.debug('WMS getLayers - configObjs', configObjs)

    configObjs = this.postProcessLayers(capabilityOptions, configObjs)

    return configObjs
  }
}

export interface WMSLayer {
  Name: string
  Title: string
  Style: any[]
  Abstract: string
  Layer?: Array<WMSLayer>
  MaxScaleDenominator?: number
  MinScaleDenominator?: number
  displayField?: string
  geometryType?: Geometry // QGIS GetProjectSettings
  opacity: number
  queryable: boolean
}

export interface WMSCapability {
  Service: {
    ContactInformation: {
      ContactPersonPrimary: {
        ContactOrganization: string
      }
    }
  }
  Capability: {
    Request: {
      GetMap: {
        DCPType: Array<{ HTTP: { Get: { OnlineResource: string } } }>
      }
    }
    Layer: WMSLayer | { Layer: Array<WMSLayer> }
  }
}
